import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import EconomicGroupStoreFields from "components/_refactor/fields/economic-group-store-fields";
import ProductsChoose from "./products-choose";
import InvoiceChoose from "./invoice-choose";
import { IsMobile } from "utils/is-mobile";
import TableSolicitacaoDevolucao from "../components/table-product";
import EditProductDialog from "../components/dialog-edit";

const RETURN_TYPES = [
  { name: "Devolução por código/descrição do produto", value: "1" },
  { name: "Devolução por nota fiscal", value: "2" },
];

export default function SolicitarDevolucaoStep1({
  fieldsData,
  setFieldsData,
  plataformList,
  plataform,
  setPlataform,
  isLoading,
  setIsLoading,
  setSolicitacao,
  setStep,
  item,
  setItem,
  setItemsWithMotivos,
  onBack,
  motivoGeral,
  setMotivoGeral,
  defeitoGeral,
  setDefeitoGeral,
  solicitacao,
}) {
  const readOnly = !!solicitacao;
  const [loadingFields, setLoadingFields] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [returnType, setReturnType] = useState("1");
  const [reasonsList, setReasonsList] = useState([]);
  const [defectList, setDefectList] = useState([]);
  const [defectByReason, setDefectByReason] = useState([]);
  const [searchReason, setSearchReason] = useState(false);
  const [searchDefect, setSearchDefect] = useState(false);

  const [addedProductsList, setAddedProductsList] = useState(item || []);

  const [toggleFormDialog, setToggleFormDialog] = useState();
  const [formProductSelected, setFormProductSelected] = useState(null);

  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const requiresEvidenceByMotivo = (motivo) => motivo?.ObrigaEvidencia === true || motivo?.RequiredEvidence === 1 || motivo?.RequiredEvidence === true;
  const motivoRequerEvidencia = requiresEvidenceByMotivo(motivoGeral);
  const productWithoutEvidence = motivoRequerEvidencia && addedProductsList.some(
    (p) => !p.images || p.images.length === 0
  );
  const requiresDefect = (defectByReason || []).length > 0;

  const handleGetReasons = async () => {
    setSearchReason(true);
    try {
      const response = await Api.get(
        "list-reason",
        "trocasDevolucoes",
        "",
        true
      );
      setReasonsList(response?.Result?.ListaMotivos || []);
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    } finally {
      setSearchReason(false);
    }
  };

  const handleGetDefects = async () => {
    setSearchDefect(true);
    try {
      const response = await Api.get(
        "list-defect",
        "trocasDevolucoes",
        "?page=0",
        true
      );
      setDefectList(response?.Result?.ListaDefeitos || []);
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    } finally {
      setSearchDefect(false);
    }
  };

  useEffect(() => {
    handleGetReasons();
    handleGetDefects();
  }, []);

  useEffect(() => {
    const byReason = (defectList || []).filter(
      (defect) => defect.IdMotivo === motivoGeral?.IdMotivo
    );
    setDefectByReason(byReason);
    if (byReason.length === 0) setDefeitoGeral?.(null);
  }, [motivoGeral, defectList]);

  const handleSubmit = async () => {
    if (readOnly) {
      setStep(2);
      return;
    }
    if (!motivoGeral) {
      snackbar.showMessage("Selecione o motivo da solicitação.", { severity: "error" });
      return;
    }
    if (requiresDefect && !defeitoGeral) {
      snackbar.showMessage("Selecione o defeito da solicitação.", { severity: "error" });
      return;
    }
    setIsLoading(true);
    try {
      const itens = addedProductsList.map((product) => ({
        idProduto: String(product.idProduto),
        quantidade: +product.quantity,
      }));

      const productImages = addedProductsList.map((product) => ({
        idProduto: product.idProduto,
        images: product.images || [],
      }));

      setItem(itens);

      const createRequestResponse = await Api.post(
        "create-request-reason",
        "trocasDevolucoes",
        {
          idPessoaLoja: fieldsData.idPessoa,
          idGrupoEconomico: fieldsData.idGrupoEconomico,
          idMarca: plataform.id,
          idDevolucaoMotivo: motivoGeral.IdMotivo,
          idDevolucaoDefeito: defeitoGeral?.IdDefeito ?? null,
          itens,
        },
        true
      );

      if (createRequestResponse.Success) {
        const solicitationItems =
          createRequestResponse.Result.ListSolicitacaoItens;

        for (const productImage of productImages) {
          if (productImage.images?.length > 0) {
            const solicitationItem = solicitationItems.find(
              (item) => item.IdProduto === productImage.idProduto
            );

            if (solicitationItem) {
              for (const image of productImage.images) {
                await Api.post(
                  "update-request-images-item",
                  "trocasDevolucoes",
                  {
                    base64: image.base64.split(",")[1],
                    idSolicitacaoItem: solicitationItem.IdSolicitacaItem,
                    nomeArquivo: image.name,
                    extensao: image.extension,
                  },
                  true
                );
              }
            }
          }
        }
      }

      setSolicitacao(createRequestResponse.Result);
      setItemsWithMotivos?.(addedProductsList.map((p) => ({
        ...p,
        motivo: motivoGeral,
        defeito: defeitoGeral,
      })));
      setStep(2);
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setPlataform(plataformList[0].plataform);
    setReturnType("1");
    setAddedProductsList([]);
    setMotivoGeral?.(null);
    setDefeitoGeral?.(null);
    setTimeout(() => {
      setClearFilters(false);
    }, 100);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <EconomicGroupStoreFields
              onFieldsChange={setFieldsData}
              onFieldsLoad={setLoadingFields}
              disableAll={readOnly || loadingFields.length > 0}
              clearFilters={clearFilters}
              defaultGroupName={fieldsData.idGrupoEconomico}
            />

            <Grid item xs={12} md={6} lg={3}>
              <FormGroup>
                <AutoCompleteField
                  label="Plataforma"
                  onChange={(value) => {
                    setPlataform(value);
                  }}
                  value={plataform}
                  options={plataformList}
                  disabled={readOnly || loadingFields.length > 0}
                  optionLabelKey="plataform"
                  optionIdKey="id"
                />
              </FormGroup>
            </Grid>

            <Grid item sx={12} md={6} lg={9} style={{ marginTop: "1.25rem" }}>
              <FormGroup>
                <RadioGroup
                  aria-label="opções de pesquisa"
                  required
                  name="type"
                  value={returnType}
                  disabled={readOnly || loadingFields.length > 0}
                  style={{
                    display: "flex",
                    margin: "0",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                  onChange={(e) => {
                    setReturnType(e.target.value);
                  }}
                >
                  {RETURN_TYPES.map((type) => {
                    return (
                      <FormControlLabel
                        disabled={readOnly || loadingFields.length > 0}
                        key={type?.value}
                        value={type?.value}
                        checked={returnType === type?.value}
                        control={<Radio color="primary" />}
                        label={type.name}
                      />
                    );
                  })}
                </RadioGroup>
              </FormGroup>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <FormGroup>
                <AutoCompleteField
                  label="Motivo da solicitação *"
                  onChange={(value) => setMotivoGeral?.(value)}
                  value={motivoGeral}
                  options={reasonsList}
                  disabled={readOnly || loadingFields.length > 0 || searchReason}
                  loading={searchReason}
                  optionLabelKey="Motivo"
                  optionIdKey="IdMotivo"
                />
              </FormGroup>
            </Grid>
            {defectByReason.length > 0 && (
              <Grid item xs={12} md={6} lg={3}>
                <FormGroup>
                  <AutoCompleteField
                    label="Defeito da solicitação *"
                    onChange={(value) => setDefeitoGeral?.(value)}
                    value={defeitoGeral}
                    options={defectByReason}
                    disabled={readOnly || loadingFields.length > 0 || searchDefect}
                    loading={searchDefect}
                    optionLabelKey="Defeito"
                    optionIdKey="IdDefeito"
                  />
                </FormGroup>
              </Grid>
            )}

            {motivoRequerEvidencia && addedProductsList.length > 0 && productWithoutEvidence && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  style={{ color: "#ed5c5c", marginTop: "0.5rem" }}
                >
                  <i
                    className="fa fa-exclamation-circle"
                    aria-hidden="true"
                    style={{ fontSize: "1rem", marginRight: "0.5rem" }}
                  />
                  O motivo selecionado exige evidência. Edite os produtos na lista e informe a evidência (imagem ou vídeo) para cada um.
                </Typography>
              </Grid>
            )}

            {((fieldsData.idGrupoEconomico && fieldsData.idPessoa) ||
              fieldsData.cnpj) &&
              plataform && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {returnType === "1" && !readOnly && (
                    <Grid item xs={12}>
                      <ProductsChoose
                        fieldsData={fieldsData}
                        loadingFields={loadingFields}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        addedProductsList={addedProductsList}
                        setAddedProductsList={setAddedProductsList}
                        clearFilters={handleClearFilters}
                        motivoGeral={motivoGeral}
                        motivoRequerEvidencia={motivoRequerEvidencia}
                      />
                    </Grid>
                  )}

                  {returnType === "2" && !readOnly && (
                    <Grid item xs={12}>
                      <InvoiceChoose
                        fieldsData={fieldsData}
                        loadingFields={loadingFields}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        addedProductsList={addedProductsList}
                        setAddedProductsList={setAddedProductsList}
                        clearFilters={handleClearFilters}
                        motivoRequerEvidencia={motivoRequerEvidencia}
                      />
                    </Grid>
                  )}
                </>
              )}

            {fieldsData.idGrupoEconomico && !fieldsData.idPessoa && (
              <Typography
                variant="h5"
                component="h5"
                style={{ color: "grey", marginTop: "1rem" }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{
                    fontSize: "1rem",
                    marginRight: "0.5rem",
                  }}
                />{" "}
                É necessário selecionar uma Razão Social / Loja para realizar a
                pesquisa.
              </Typography>
            )}
          </Grid>
        </CardContent>
      </Card>

      {addedProductsList?.length > 0 && (
        <TableSolicitacaoDevolucao
          setSelectedProduct={setFormProductSelected}
          setToggleFormDialog={setToggleFormDialog}
          products={addedProductsList}
          readOnly={readOnly}
        />
      )}

      {!!toggleFormDialog && !readOnly && (
        <EditProductDialog
          formType={toggleFormDialog}
          onClose={() => setToggleFormDialog(null)}
          selectedProduct={formProductSelected}
          productsList={addedProductsList}
          setProductsList={setAddedProductsList}
          motivoRequerEvidencia={motivoRequerEvidencia}
        />
      )}

      <Grid
        container
        style={{ justifyContent: "flex-end", margin: "1rem 0" }}
        spacing={2}
      >
        <Grid item xs={6} md={3}>
          <Button variant="outlined" onClick={onBack}>
            Voltar
          </Button>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !readOnly &&
              (addedProductsList?.length < 1 ||
                isLoading ||
                loadingFields.length > 0 ||
                !fieldsData.cnpj ||
                !motivoGeral ||
                (requiresDefect && !defeitoGeral) ||
                productWithoutEvidence)
            }
          >
            Avançar
          </Button>
        </Grid>
      </Grid>
    </>
  );
}
